import '../icon';
import { defineCustomElement, updateAttribute, getIntegerAttribute, setClass, NectaryElement, getRect, getReactEventHandler, isTargetEqual, getTargetIndexInParent } from '../utils';
const templateHTML = '<style>:host{display:inline-block;vertical-align:middle;outline:0}#wrapper{display:flex;justify-content:center;gap:8px;width:100%;height:var(--sinch-local-size);--sinch-local-size:24px}button{all:initial;position:relative;display:flex;justify-content:center;align-items:center;min-width:var(--sinch-local-size);height:var(--sinch-local-size);cursor:pointer;border-radius:var(--sinch-comp-pagination-shape-radius);user-select:none;--sinch-global-color-icon:var(--sinch-comp-pagination-color-default-icon-default)}button:focus-visible::before{content:"";position:absolute;inset:-3px;border:2px solid var(--sinch-comp-pagination-color-default-outline-focus);border-radius:calc(var(--sinch-comp-pagination-shape-radius) + 3px);pointer-events:none}button:disabled{--sinch-global-color-icon:var(--sinch-comp-pagination-color-disabled-icon-initial);cursor:initial}button:enabled:hover{background-color:var(--sinch-comp-pagination-color-default-background-hover)}button>*{display:block;overflow:hidden;pointer-events:none}.page{font:var(--sinch-comp-pagination-font-default-page-number);color:var(--sinch-comp-pagination-color-default-text-initial);background-color:var(--sinch-comp-pagination-color-default-background-initial);padding:0 4px;box-sizing:border-box}.page.dots>span{display:none}.page.dots::after{content:"..."}.page.active{font:var(--sinch-comp-pagination-font-checked-page-number);background-color:var(--sinch-comp-pagination-color-checked-background-initial);pointer-events:none;cursor:initial}.page.active:hover{background-color:var(--sinch-comp-pagination-color-checked-background-hover)}.page.hidden{display:none}#left,#right{--sinch-icon-size:24px}</style><div id="wrapper"><button id="left"><sinch-icon id="icon-left" name="keyboard_arrow_left"></sinch-icon></button><button class="page"><span>1</span></button><button class="page active"><span>2</span></button><button class="page"><span>3</span></button><button class="page"><span>4</span></button><button class="page"><span>5</span></button><button class="page dots"><span>6</span></button><button class="page"><span>20</span></button><button id="right"><sinch-icon id="icon-right" name="keyboard_arrow_right"></sinch-icon></button></div>';
const NUM_BUTTONS = 7;
const MIDDLE_BTN_INDEX = Math.floor(NUM_BUTTONS / 2);
const FIRST_BTN_INDEX = 0;
const LAST_BTN_INDEX = NUM_BUTTONS - 1;
const DOTS_LEFT_INDEX = 1;
const DOTS_RIGHT_INDEX = LAST_BTN_INDEX - 1;
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-pagination', class extends NectaryElement {
  #$left;
  #$right;
  #$buttons;
  #$wrapper;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$left = shadowRoot.querySelector('#left');
    this.#$right = shadowRoot.querySelector('#right');
    this.#$buttons = shadowRoot.querySelectorAll('.page');
    this.#$wrapper = shadowRoot.querySelector('#wrapper');
  }
  connectedCallback() {
    this.#onValueChange();
    this.#$wrapper.addEventListener('click', this.#onButtonClick);
    this.addEventListener('-change', this.#onChangeReactHandler);
  }
  disconnectedCallback() {
    this.#$wrapper.removeEventListener('click', this.#onButtonClick);
    this.removeEventListener('-change', this.#onChangeReactHandler);
  }
  static get observedAttributes() {
    return ['max', 'value'];
  }
  attributeChangedCallback(name) {
    switch (name) {
      case 'value':
      case 'max':
        {
          this.#onValueChange();
          break;
        }
    }
  }
  set value(val) {
    updateAttribute(this, 'value', val);
  }
  get value() {
    return getIntegerAttribute(this, 'value', 0);
  }
  set max(val) {
    updateAttribute(this, 'max', val);
  }
  get max() {
    return getIntegerAttribute(this, 'value', 0);
  }
  #onValueChange() {
    const value = getIntegerAttribute(this, 'value', 0) - 1;
    const max = Math.max(0, getIntegerAttribute(this, 'max', 0));
    const valueOffset = Math.min(Math.max(0, value - MIDDLE_BTN_INDEX), Math.max(0, max - NUM_BUTTONS));
    for (let i = 0; i < this.#$buttons.length; i++) {
      const $b = this.#$buttons[i];
      if (value < 3) {
        setClass($b, 'active', value === i);
      } else if (value >= max - MIDDLE_BTN_INDEX) {
        setClass($b, 'active', i + valueOffset === value);
      } else {
        setClass($b, 'active', i === MIDDLE_BTN_INDEX);
      }
      if (max > NUM_BUTTONS) {
        setClass($b, 'dots', i === DOTS_LEFT_INDEX && value > MIDDLE_BTN_INDEX || i === DOTS_RIGHT_INDEX && value <= max - DOTS_RIGHT_INDEX);
      }
      setClass($b, 'hidden', i >= max);
      const btnText = $b.firstElementChild;
      if (btnText != null) {
        btnText.textContent = i === FIRST_BTN_INDEX ? '1' : i === LAST_BTN_INDEX ? String(max) : String(i + 1 + valueOffset);
      }
    }
    const isValueBad = value < 0 || value >= max;
    this.#$left.disabled = isValueBad || value === 0;
    this.#$right.disabled = isValueBad || value === max - 1;
  }
  #onButtonClick = e => {
    e.stopPropagation();
    const value = Math.max(getIntegerAttribute(this, 'value', 0) - 1);
    const max = Math.max(0, getIntegerAttribute(this, 'max', 0));
    if (isTargetEqual(e, this.#$left)) {
      return this.#dispatchChangeEvent(Math.max(value - 1, 0));
    }
    if (isTargetEqual(e, this.#$right)) {
      return this.#dispatchChangeEvent(Math.min(value + 1, max));
    }
    const btnIndex = getTargetIndexInParent(e, this.#$wrapper) - 1;
    if (btnIndex >= FIRST_BTN_INDEX && btnIndex <= LAST_BTN_INDEX) {
      if (btnIndex === FIRST_BTN_INDEX) {
        return this.#dispatchChangeEvent(0);
      }
      if (btnIndex === LAST_BTN_INDEX) {
        return this.#dispatchChangeEvent(max);
      }
      if (btnIndex === DOTS_LEFT_INDEX && max > NUM_BUTTONS && value > MIDDLE_BTN_INDEX) {
        return this.#dispatchChangeEvent(Math.floor(value / 2));
      }
      if (btnIndex === DOTS_RIGHT_INDEX && max > NUM_BUTTONS && value <= max - DOTS_RIGHT_INDEX) {
        return this.#dispatchChangeEvent(Math.floor((max - value) / 2 + value));
      }
      return this.#dispatchChangeEvent(btnIndex + Math.min(Math.max(0, value - MIDDLE_BTN_INDEX), Math.max(0, max - NUM_BUTTONS)));
    }
  };
  #clamp(value) {
    const max = getIntegerAttribute(this, 'max', 0);
    return Math.max(0, Math.min(max - 1, value)) + 1;
  }
  #dispatchChangeEvent(value) {
    const detail = this.#clamp(value);
    this.dispatchEvent(new CustomEvent('-change', {
      detail
    }));
  }
  #onChangeReactHandler = e => {
    getReactEventHandler(this, 'on-change')?.(e);
  };
  get focusable() {
    return true;
  }
  focus() {
    this.#$left.focus();
  }
  blur() {
    this.#$left.blur();
    this.#$right.blur();
    this.#$buttons.forEach($b => $b.blur());
  }
  get prevButtonRect() {
    return getRect(this.#$left);
  }
  get nextButtonRect() {
    return getRect(this.#$right);
  }
  nthButtonRect(index) {
    const btn = this.#$buttons[index];
    return btn == null ? null : getRect(btn);
  }
});